import { useState, useMemo, useCallback } from 'react';
import { FullProject } from '../types';
import { computeNotifications, AppNotification, NotificationType } from '../utils/notificationUtils';

export type { AppNotification, NotificationType };

interface UseNotificationsProps {
  projects: FullProject[];
  userEmail?: string;
}

const STORAGE_KEY = 'gwantt_read_notifications';

export function useNotifications({ projects, userEmail }: UseNotificationsProps) {
  const [readIds, setReadIds] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return new Set<string>(JSON.parse(stored));
      }
    } catch (e) {
      console.warn('Failed to parse read notifications from localStorage', e);
    }
    return new Set<string>();
  });

  const saveReadIds = useCallback((newSet: Set<string>) => {
    setReadIds(newSet);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(newSet)));
    } catch (e) {
      console.warn('Failed to persist read notifications to localStorage', e);
    }
  }, []);

  const notifications = useMemo<AppNotification[]>(() => {
    return computeNotifications(projects, readIds);
  }, [projects, readIds]);

  const unreadCount = useMemo(() => {
    return notifications.filter(n => !n.read).length;
  }, [notifications]);

  const markAsRead = useCallback((notificationId: string) => {
    setReadIds(prev => {
      const next = new Set(prev);
      next.add(notificationId);
      saveReadIds(next);
      return next;
    });
  }, [saveReadIds]);

  const markAllAsRead = useCallback(() => {
    const next = new Set(readIds);
    notifications.forEach(n => next.add(n.id));
    saveReadIds(next);
  }, [notifications, readIds, saveReadIds]);

  const clearReadHistory = useCallback(() => {
    saveReadIds(new Set<string>());
  }, [saveReadIds]);

  return {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearReadHistory
  };
}
